import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import Button from '../components/ui/Button';

// Allocation Review (migration 077): each DC's share of D365 invoiced units over rolling
// 13/26/52 complete weeks, against the forecast allocation (commercial.TransportAllocation).
// "Sync to actuals" drafts a proposal (guarded: deadband, longer window must agree, capped
// move) — sales adjusts + submits, planning approves, and only approval writes the live
// allocation. Nothing on this page changes the forecast directly.
//
// Brand split (migration 079): with Bhuja selected, "Sync to actuals" drafts a BRAND proposal —
// Bhuja moves toward its own invoiced share and Infuzions is derived so each DC still totals the
// customer % at the forecast brand mix. Infuzions is view-only; it is never set directly.

const WINDOWS = [13, 26, 52];
const pct = (v, d = 1) => (v == null ? '—' : `${v.toFixed(d)}%`);
const num = (v) => (v == null ? '—' : Math.round(v).toLocaleString('en-AU'));
const when = (s) => (s ? new Date(s).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const day = (s) => (s ? new Date(s).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

function rag(gap) {
  if (gap == null) return { c: 'var(--text-muted)', l: '—' };
  const a = Math.abs(gap);
  if (a < 0.5) return { c: 'var(--success)', l: 'OK' };
  if (a <= 1.5) return { c: 'var(--warn)', l: 'Watch' };
  return { c: 'var(--danger)', l: 'Review' };
}

const card = { border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12 };
const th = { textAlign: 'right', padding: '6px 8px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' };
const td = { textAlign: 'right', padding: '6px 8px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };

export default function AllocationReview() {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const [customer, setCustomer] = useState('CUS0044');
  const [brand, setBrand] = useState('');            // '' = all brands
  const [weeks, setWeeks] = useState(13);
  const [cap, setCap] = useState(1);
  const [edits, setEdits] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [reasons, setReasons] = useState({});

  const custQ = useQuery({ queryKey: ['alloc-customers'], queryFn: () => apiFetch('/allocation-review/customers').then((r) => r.json()) });
  const revQ = useQuery({
    queryKey: ['alloc-review', customer, brand],
    queryFn: async () => {
      const q = `customer=${encodeURIComponent(customer)}${brand ? `&brand=${encodeURIComponent(brand)}` : ''}`;
      const r = await apiFetch(`/allocation-review?${q}`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? r.status);
      return r.json();
    },
  });
  const propQ = useQuery({
    queryKey: ['alloc-proposal', customer],
    queryFn: () => apiFetch(`/allocation-proposal?customer=${encodeURIComponent(customer)}`).then((r) => r.json()),
  });
  const rev = revQ.data;
  const open = propQ.data?.open ?? null;
  useEffect(() => { setEdits({}); setNote(open?.note ?? ''); }, [open?.proposalId, open?.status, brand]);
  useEffect(() => { setReasons({}); setErr(null); setBrand(''); }, [customer]);

  const refresh = () => { qc.invalidateQueries({ queryKey: ['alloc-review', customer] }); qc.invalidateQueries({ queryKey: ['alloc-proposal', customer] }); };
  async function call(path, body) {
    setBusy(true); setErr(null);
    try {
      const r = await apiFetch(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error ?? `Request failed (${r.status})`);
      return j;
    } catch (e) { setErr(String(e.message ?? e)); return null; } finally { setBusy(false); refresh(); }
  }
  async function sync() {
    const j = await call('/allocation-proposal', { customer, weeks, capPts: cap, scope: brand === 'Bhuja' ? 'BRAND' : 'CUSTOMER' });
    if (j?.reasons) setReasons(Object.fromEntries(j.reasons.map((x) => [`${x.dcId}|${x.brand ?? ''}`, x.reason])));
  }
  async function saveEdits() {
    const lines = Object.entries(edits).map(([dcId, v]) => ({ dcId: Number(dcId), proposedPct: Number(v), brand: lineBrand }));
    return call(`/allocation-proposal/${open.proposalId}/lines`, { lines, note });
  }
  async function submit() { if (await saveEdits()) await call(`/allocation-proposal/${open.proposalId}/submit`); }

  // A proposal shows on the view it belongs to: customer lines on "All brands", brand lines on
  // that brand. Only the customer lines or the Bhuja lines take edits.
  const lineBrand = brand;
  const showProp = !!open && (open.scope === 'BRAND' ? brand === 'Bhuja' || brand === 'Infuzions' : !brand);
  const lines = useMemo(() => new Map((showProp ? open.lines : []).filter((l) => l.brand === lineBrand).map((l) => [l.dcId, l])), [open, showProp, lineBrand]);
  const propVal = (dcId) => (edits[dcId] !== undefined ? Number(edits[dcId]) : lines.get(dcId)?.proposedPct);
  const propTotal = showProp ? Math.round([...lines.keys()].reduce((s, d) => s + (propVal(d) || 0), 0) * 10) / 10 : null;
  const editable = showProp && open?.status === 'Draft' && propQ.data?.canPropose && brand !== 'Infuzions';
  const mix = rev?.brandMix;
  const syncBlock = open?.status === 'Submitted' ? 'A proposal is awaiting sign-off'
    : brand && brand !== 'Bhuja' ? `${brand} is derived from Bhuja so each DC keeps the customer % - select Bhuja to propose a split`
    : brand === 'Bhuja' && !(mix?.bhuja > 0 && mix?.infuzions > 0) ? 'No Bhuja/Infuzions forecast mix for this customer yet'
    : null;
  const canApprove = open?.status === 'Submitted' && propQ.data?.canApprove;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 11px' }}>
        Each DC's share of <b>D365 invoiced units</b> (job lots and returns excluded) over the last complete weeks, against the forecast allocation.
        Invoiced share follows our own supply and WA ships in fewer, bigger loads, so read the 26- and 52-week columns before acting.
        <b> Sync to actuals</b> only drafts a proposal; planning approves it before the forecast changes.
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={customer} onChange={(e) => setCustomer(e.target.value)} style={{ fontSize: 12, padding: '5px 8px' }}>
          {(custQ.data?.customers ?? []).map((c) => (
            <option key={c.customerCode} value={c.customerCode}>{c.name ?? c.customerCode} ({c.customerCode}){c.hasActuals ? '' : ' — no DC mapping yet'}</option>
          ))}
        </select>
        <select value={brand} onChange={(e) => setBrand(e.target.value)} aria-label="Brand" style={{ fontSize: 12, padding: '5px 8px' }}>
          <option value="">All brands</option>
          {(rev?.brands ?? []).map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sync basis</span>
        <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} style={{ fontSize: 12, padding: '5px 8px' }}>
          {WINDOWS.map((w) => <option key={w} value={w}>{w} weeks</option>)}
        </select>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Max move per DC</span>
        <select value={cap} onChange={(e) => setCap(Number(e.target.value))} style={{ fontSize: 12, padding: '5px 8px' }}>
          {[0.5, 1, 2].map((c) => <option key={c} value={c}>{c} pt</option>)}
        </select>
        <span style={{ flex: 1 }} />
        {propQ.data?.canPropose && (
          <Button size="sm" variant="primary" disabled={busy || !rev || !!syncBlock} onClick={sync}
            title={syncBlock ?? (brand === 'Bhuja' ? 'Draft a Bhuja/Infuzions split from Bhuja invoiced shares' : 'Draft a proposal from the rolling invoiced shares')}>
            {brand === 'Bhuja' ? 'Sync brand split' : 'Sync to actuals'}
          </Button>
        )}
      </div>

      {brand && mix && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 11px' }}>
          <b>{brand}</b> view: Current is the {brand} %{rev?.brandRows ? '' : ' (no brand split yet, so the customer %)'}.
          Forecast mix {day(mix.from)}–{day(mix.to)}: Bhuja {pct(mix.bhuja * 100, 0)} · Infuzions {pct(mix.infuzions * 100, 0)}{mix.other > 0.0005 ? ` · other ${pct(mix.other * 100, 0)} (stays on the customer %)` : ''}.
          Bhuja is set; Infuzions is derived so each DC still totals the customer % and the forecast's DC totals do not move.
        </div>
      )}
      {open && !showProp && (
        <div style={{ fontSize: 12, color: 'var(--warn-text)' }}>
          Proposal #{open.proposalId} ({open.scope === 'BRAND' ? 'brand split' : 'customer'}, {open.status}) is open — {open.scope === 'BRAND' ? 'select Bhuja or Infuzions' : 'select All brands'} to see it.
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: 'var(--danger)', background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 'var(--radius)', padding: '6px 10px' }}>{err}</div>}
      {revQ.isError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{String(revQ.error.message)}</div>}

      {rev && (
        <div style={card}>
          <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, flexWrap: 'wrap' }}>
            <span>Weeks to {day(rev.weekTo)} (week starting)</span>
            {WINDOWS.map((w) => <span key={w}>{w}w from {day(rev.windows[w].from)}: {num(rev.windows[w].units)} units</span>)}
            <span>Actuals synced {when(rev.lastSync?.syncedAt)} · D365 to {day(rev.lastSync?.sourceMaxDate)}</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>DC</th>
                {brand && <th style={th}>Customer</th>}
                <th style={th}>Current</th>
                {WINDOWS.map((w) => <th key={w} style={{ ...th, background: w === weeks ? 'var(--accent-soft)' : undefined }}>{w}w share</th>)}
                <th style={th} title="Retail scan share of the state this DC alone serves (WA)">{weeks}w scan</th>
                <th style={th}>{weeks}w units</th>
                <th style={th}>Gap ({weeks}w)</th>
                <th style={th}>Status</th>
                {showProp && <th style={th}>Proposed</th>}
                {showProp && <th style={th}>Change</th>}
              </tr>
            </thead>
            <tbody>
              {rev.lanes.map((l) => {
                const s = l.share[weeks];
                const gap = s == null ? null : s - l.currentPct;
                const r = rag(gap);
                const p = propVal(l.dcId);
                const line = lines.get(l.dcId);
                return (
                  <tr key={l.dcId} title={reasons[`${l.dcId}|${lineBrand}`] ?? ''}>
                    <td style={{ ...td, textAlign: 'left' }}>{l.dc} <span style={{ color: 'var(--text-muted)' }}>{l.state}{l.warehouse === '17A' ? ' · 3PL' : ''}</span></td>
                    {brand && <td style={{ ...td, color: 'var(--text-muted)' }}>{pct(l.customerPct)}</td>}
                    <td style={td}>{pct(l.currentPct)}</td>
                    {WINDOWS.map((w) => <td key={w} style={{ ...td, background: w === weeks ? 'var(--accent-soft)' : undefined }}>{pct(l.share[w])}</td>)}
                    <td style={{ ...td, color: l.scanShare?.[weeks] == null ? 'var(--text-muted)' : 'inherit' }}>{pct(l.scanShare?.[weeks])}</td>
                    <td style={td}>{num(l.units[weeks])}</td>
                    <td style={td}>{gap == null ? '—' : `${gap > 0 ? '+' : ''}${gap.toFixed(1)}`}</td>
                    <td style={{ ...td, color: r.c, fontWeight: 600 }}>{r.l}</td>
                    {showProp && (
                      <td style={td}>
                        {editable
                          ? <input type="number" step="0.1" min="0" max="100" value={edits[l.dcId] ?? line?.proposedPct ?? ''}
                              onChange={(e) => setEdits((x) => ({ ...x, [l.dcId]: e.target.value }))}
                              style={{ width: 64, textAlign: 'right', fontSize: 12, padding: '2px 4px' }} />
                          : pct(p)}
                      </td>
                    )}
                    {showProp && <td style={{ ...td, color: p == null || Math.abs(p - l.currentPct) < 0.05 ? 'var(--text-muted)' : 'inherit' }}>
                      {p == null ? '—' : `${p - l.currentPct > 0 ? '+' : ''}${(p - l.currentPct).toFixed(1)}`}</td>}
                  </tr>
                );
              })}
              <tr>
                <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>Total</td>
                {brand && <td style={td} />}
                <td style={{ ...td, fontWeight: 600 }}>{pct(rev.currentTotal)}</td>
                <td colSpan={WINDOWS.length + 4} style={td} />
                {showProp && <td style={{ ...td, fontWeight: 600, color: propTotal === 100 ? 'inherit' : 'var(--danger)' }}>{pct(propTotal)}</td>}
                {showProp && <td style={td} />}
              </tr>
            </tbody>
          </table>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <span>Job lots left out: {WINDOWS.map((w) => `${num(rev.windows[w].jobLotUnits)} units (${w}w)`).join(' · ')}. Clearance lines stay in.</span>
            {rev.scanSource
              ? <span>Scan: {rev.scanSource === 'quantium' ? 'Quantium store-level (in-store + online by fulfilling store)' : 'Fabric STORE'}, to week ending {day(rev.windows[weeks].scanLastWeek)}. Shown only for a DC that alone serves its state (WA).</span>
              : <span>No retail scan feed for this customer.</span>}
          </div>
          {rev.unmapped.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--warn-text)', marginTop: 8 }}>
              Not in any lane (13w): {rev.unmapped.map((u) => `${u.deliveryName} ${num(u.units)} units`).join(' · ')} — add a DC mapping to include it.
            </div>
          )}
        </div>
      )}

      {showProp && (
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12 }}>
            <b>Proposal #{open.proposalId}</b>{open.scope === 'BRAND' ? ' (brand split)' : ''} · {open.status} · {open.windowWeeks}-week basis, cap {open.capPts}pt ·
            drafted by {open.createdByName ?? '—'} {when(open.createdAt)}
            {open.submittedAt && <> · submitted by {open.submittedByName} {when(open.submittedAt)}</>}
          </div>
          {editable
            ? <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Why this change (shown to planning)"
                style={{ fontSize: 12, padding: 6, fontFamily: 'inherit' }} />
            : open.note && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Note: {open.note}</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {open.scope === 'BRAND' && brand === 'Infuzions' && open.status === 'Draft' && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Edit on the Bhuja view; Infuzions follows.</span>}
            {editable && <Button size="sm" disabled={busy} onClick={saveEdits}>Save draft</Button>}
            {editable && <Button size="sm" variant="primary" disabled={busy || propTotal !== 100} onClick={submit}
              title={propTotal !== 100 ? 'Proposed lanes must add to 100%' : 'Send to planning for sign-off'}>Submit for sign-off</Button>}
            {editable && <Button size="sm" variant="ghost" disabled={busy} onClick={() => call(`/allocation-proposal/${open.proposalId}/discard`)}>Discard</Button>}
            {canApprove && <Button size="sm" variant="primary" disabled={busy}
              onClick={() => call(`/allocation-proposal/${open.proposalId}/approve`, { note })}>Approve — update forecast allocation</Button>}
            {canApprove && <Button size="sm" disabled={busy}
              onClick={() => { const n = window.prompt('Reason for rejecting?'); if (n !== null) call(`/allocation-proposal/${open.proposalId}/reject`, { note: n }); }}>Reject</Button>}
            {open.status === 'Submitted' && !propQ.data?.canApprove && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Awaiting planning sign-off.</span>}
          </div>
        </div>
      )}

      {(propQ.data?.recent ?? []).filter((p) => p.status !== 'Draft' && p.status !== 'Submitted').length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          History: {(propQ.data.recent).filter((p) => p.status !== 'Draft' && p.status !== 'Submitted').map((p) =>
            `#${p.proposalId} ${p.status}${p.decidedByName ? ` by ${p.decidedByName}` : ''} ${day(p.decidedAt ?? p.createdAt)}`).join(' · ')}
        </div>
      )}
    </div>
  );
}
